// The catalogue property (design §33.3.1) is declared in `../template/catalogue.ts`, beside `CataloguedAgentTemplate`;
// it is re-exported here because this module is what the package barrel re-exports whole (plan-021 ED-30).
export { VOICE_AGENT_AUDIENCE } from "../template/catalogue.js";
export { VOICE_AGENT_PASS_EVENT_CODE } from "../conversation/conversation.js";
// ---- product constants (OD-14) -----------------------------------------------------------------------------------
/** Completed passes per conversation, passes back included (OD-14). The seventh is refused `pass_limit_reached`. */
export const VOICE_AGENT_PASS_LIMIT = 6;
/** How many VoiceAgents may wait on the pass stack for a pass back (OD-14). */
export const VOICE_AGENT_PASS_STACK_DEPTH = 3;
/** Pass targets one VoiceAgent may list (OD-14); a ninth is refused at save (`pass_target_limit_exceeded`). */
export const VOICE_AGENT_PASS_TARGET_LIMIT = 8;
/** The longest "when to pass" text, in characters (ED-32). */
export const VOICE_AGENT_PASS_WHEN_CHARACTER_LIMIT = 240;
/** The longest "what to tell" note of a warm pass, in characters (ED-32). */
export const VOICE_AGENT_PASS_NOTE_CHARACTER_LIMIT = 240;
/** The longest target display name the document carries, in characters, after sanitising (design §33.4.2). */
export const VOICE_AGENT_PASS_DISPLAY_NAME_CHARACTER_LIMIT = 80;
/** The pass summary's output budget, in model tokens (ED-15; the handoff summary's figure). */
export const VOICE_AGENT_PASS_SUMMARY_TOKEN_LIMIT = 60;
// ---- vocabularies ------------------------------------------------------------------------------------------------
/** Warm: the target is told a short pass summary. Cold: nothing; the target greets. Copy: "With a summary" / "Straight away". */
export const VOICE_AGENT_PASS_MODE = ["warm", "cold"];
/** How a VoiceAgent stint began — `voice_agent_stint.began_by` (migration 0167). Stint 1 is always `entry`. */
export const VOICE_AGENT_STINT_BEGINNING = ["entry", "warm_pass", "cold_pass", "pass_back"];
/**
 * The pass target keys (ED-3): `target_1` … `target_8`, one per link, assigned once at the first save as the lowest free
 * number ({@link targetKeyAssigned}) and kept in the answers, so reordering or removing an entry never renames another.
 * The model calls `pass_to_voice_agent` with one of them.
 */
export const VOICE_AGENT_PASS_TARGET_KEY = [
    "target_1",
    "target_2",
    "target_3",
    "target_4",
    "target_5",
    "target_6",
    "target_7",
    "target_8",
];
/** One key per target the limit allows — the key list cannot drift from the limit. */
const TARGET_KEY_MATCHES_LIMIT = true;
void TARGET_KEY_MATCHES_LIMIT;
/**
 * Why a pass (or a pass back) is refused at the moment the tool is called — design §33.7, ED-11 and D-15. The first
 * failing check answers; the model reads the value in the tool result and continues.
 */
export const PASS_UNAVAILABLE_REASON = [
    "switch_off",
    "pass_limit_reached",
    "person_handoff_in_progress",
    "voice_agent_pass_in_progress",
    "target_key_unknown",
    "pass_back_unavailable",
    "target_not_found",
    "target_not_live",
    "target_cannot_receive",
    "audience_crossed",
    "target_language_unavailable",
    "target_medium_unsupported",
    "target_outside_opening_hours",
    "target_at_line_limit",
    "admission_move_refused",
    "target_configuration_unavailable",
    "pass_record_failed",
    /**
     * ADDED 2026-10-20 (append-only; plan-022, design §34.5): a pass tool call in a line whose first exclusive platform
     * tool is `end_conversation` — the VoiceAgent is ending the conversation, so the pass never runs.
     */
    "conversation_ending",
    /**
     * ADDED 2026-10-27 (append-only; plan-029 CP-DOM, DD29-7, design §40.7.5, §40.25.2): pass gate (9) — the target's
     * access list does not admit the conversation's party (it is not listed on the target's enabled list, or the target
     * is an internal VoiceAgent locked without a list). The conversation stays with the source. AMENDED 2026-10-28
     * (plan-029 ACCESS-SPLIT): a switch being off no longer refuses a listed party — the lock has its own switch.
     */
    "party_not_listed",
];
/** Which limit `pass_limit_reached` met (`detail.limit`): the passes of the conversation, or the stack's depth. */
export const VOICE_AGENT_PASS_LIMIT_KIND = ["pass_count", "stack_depth"];
/**
 * The api's refusal codes of a pass-target save (ED-32, design §33.13.1), each `422` with `error.detail.index` where it
 * names one entry. `pass_target_not_found` is one code for another workspace, another account or a deleted row, so
 * nothing leaks. Text bounds answer the existing `validation_failed`.
 */
export const PASS_TARGET_REFUSAL = [
    "pass_target_self",
    "pass_target_not_found",
    "pass_target_archived",
    "pass_target_cannot_receive",
    "pass_source_cannot_pass",
    "pass_target_audience_crossed",
    "pass_target_limit_exceeded",
    "pass_target_duplicate",
];
/** The save's warnings (ED-32) — answered beside the draft, never refusals. */
export const PASS_TARGET_WARNING = [
    "pass_target_not_live",
    "pass_target_no_shared_language",
    "pass_target_no_shared_medium",
];
// ---- model-facing text (D-19, design §33.20.3) ----------------------------------------------------------------------
/**
 * One line of the customer's own text with no control string: no newline, carriage return or other C0/C1 control, no
 * line or paragraph separator, and neither `<|` nor `|>` (the chat format's control strings, design §30.6.16). `when`
 * and `note` are refused at save when they fail it; the document schema refuses them too.
 */
// eslint-disable-next-line no-control-regex -- the point of the pattern is to refuse control characters
export const SINGLE_LINE_WITHOUT_CONTROL_STRING = /^(?![\s\S]*(?:<\||\|>))[^\u0000-\u001F\u007F-\u009F\u2028\u2029]*$/u;
/** C0/C1 controls and the two line separators — what {@link voiceAgentPassDisplayNameOf} turns into a space. */
// eslint-disable-next-line no-control-regex -- the point of the pattern is to find control characters
const CONTROL_CHARACTER = /[\u0000-\u001F\u007F-\u009F\u2028\u2029]/gu;
/** The chat format's control strings, removed from a display name. */
const CONTROL_STRING = /<\||\|>/gu;
/**
 * A target's display name as the document carries it (design §33.4.3, D-19): control characters and line separators
 * become spaces, `<|` and `|>` are removed (repeatedly, so removing one cannot form another), runs of white space become
 * one space, the ends are trimmed and the result is cut to {@link VOICE_AGENT_PASS_DISPLAY_NAME_CHARACTER_LIMIT}
 * characters (whole code points). An empty result is the caller's to refuse: a name of nothing but control text is not
 * a name.
 */
export function voiceAgentPassDisplayNameOf(name) {
    let text = name.replace(CONTROL_CHARACTER, " ");
    let before;
    do {
        before = text;
        text = text.replace(CONTROL_STRING, "");
    } while (text !== before);
    const collapsed = text.replace(/\s+/gu, " ").trim();
    const codePoint = Array.from(collapsed);
    return codePoint.length <= VOICE_AGENT_PASS_DISPLAY_NAME_CHARACTER_LIMIT
        ? collapsed
        : codePoint.slice(0, VOICE_AGENT_PASS_DISPLAY_NAME_CHARACTER_LIMIT).join("").trimEnd();
}
/** `source.mayPass ∧ target.mayReceive ∧ source.audience = target.audience`. Pure; each side read from the catalogue by code. */
export function mayPassBetween(source, target) {
    return passBetweenRefusalOf(source, target) === null;
}
/**
 * Why {@link mayPassBetween} is false, as the save's refusal code — the source cannot pass, the target cannot receive,
 * or the audience crosses, checked in that order — or `null` when a pass between the two is allowed.
 */
export function passBetweenRefusalOf(source, target) {
    if (!source.voiceAgentPass.mayPass)
        return "pass_source_cannot_pass";
    if (!target.voiceAgentPass.mayReceive)
        return "pass_target_cannot_receive";
    if (source.audience !== target.audience)
        return "pass_target_audience_crossed";
    return null;
}
/**
 * The pass stack after a move (design §33.8.2's table, in its order):
 *
 * | Move | Condition | Result |
 * |---|---|---|
 * | any | `passCount ≥ 6` | refused `pass_limit_reached` (`pass_count`) |
 * | pass to B | B is on the stack | unwind: pop every entry above B and B itself; `pass_back`, the link's mode |
 * | pass to B | link may pass back, stack 3 deep | refused `pass_limit_reached` (`stack_depth`) |
 * | pass to B | link may pass back | push the source; `warm_pass` \| `cold_pass` |
 * | pass to B | link without pass back | clear the stack; B owns the conversation (OD-13, D-8) |
 * | pass back | stack empty | refused `pass_back_unavailable` |
 * | pass back | otherwise | pop the top; `pass_back`, the mode of the link that left it |
 *
 * `pass_back` is offered exactly when the stack is not empty (D-8). A → B (back) → A is an unwind, so a cycle never
 * grows the stack, and every completed move counts toward the six. Pure; the input stack is never changed.
 */
export function passStackAfter(stack, passCount, move) {
    if (passCount >= VOICE_AGENT_PASS_LIMIT)
        return refused("pass_limit_reached", "pass_count");
    if (move.kind === "pass_back") {
        const top = stack[stack.length - 1];
        if (top === undefined)
            return refused("pass_back_unavailable");
        return moved(stack.slice(0, -1), top.voiceAgentId, "pass_back", top.mode, move.fromOrdinal);
    }
    const target = move.link.targetVoiceAgentId;
    const onStack = stack.findIndex((entry) => entry.voiceAgentId === target);
    if (onStack >= 0)
        return moved(stack.slice(0, onStack), target, "pass_back", move.link.mode, move.fromOrdinal);
    const beganBy = move.link.mode === "warm" ? "warm_pass" : "cold_pass";
    if (!move.link.mayPassBack)
        return moved([], target, beganBy, move.link.mode, move.fromOrdinal);
    if (stack.length >= VOICE_AGENT_PASS_STACK_DEPTH)
        return refused("pass_limit_reached", "stack_depth");
    const pushed = Object.freeze({
        voiceAgentId: move.fromVoiceAgentId,
        ordinal: move.fromOrdinal,
        mode: move.link.mode,
    });
    return moved([...stack, pushed], target, beganBy, move.link.mode, move.fromOrdinal);
}
function moved(stack, toVoiceAgentId, beganBy, mode, fromOrdinal) {
    return Object.freeze({ kind: "moved", stack: Object.freeze([...stack]), toVoiceAgentId, beganBy, mode, fromOrdinal });
}
function refused(reason, limit) {
    return Object.freeze(limit === undefined ? { kind: "refused", reason } : { kind: "refused", reason, limit });
}
/**
 * Gates (0)–(3) of design §33.7, in order, from memory alone — the coordinator's step 3, before the tenant read:
 *
 * - (0) a pass back with an empty stack is `pass_back_unavailable`; a pass from a VoiceAgent whose template may not pass
 *   was never offered the tool, so its argument names no offered key (`target_key_unknown`). A key outside the stint's
 *   offered list is the tool's argument check (RT-TOOL), before these facts exist.
 * - (1) the platform switch; (2) the pass limit and, when the move would push, the stack depth (`passStackAfter`);
 * - (3) a person handoff in progress, then another platform tool earlier in this line.
 */
export function voiceAgentPassConversationGateOf(facts) {
    if (facts.move.kind === "pass_back" && facts.stack.length === 0)
        return unavailable("pass_back_unavailable");
    if (facts.move.kind === "pass" && !facts.source.voiceAgentPass.mayPass)
        return unavailable("target_key_unknown");
    if (!facts.switchOn)
        return unavailable("switch_off");
    const outcome = passStackAfter(facts.stack, facts.passCount, facts.move);
    if (outcome.kind === "refused")
        return unavailable(outcome.reason, outcome.limit);
    if (facts.personHandoffInProgress)
        return unavailable("person_handoff_in_progress");
    if (facts.platformToolEarlierInLine)
        return unavailable("voice_agent_pass_in_progress");
    return Object.freeze({ kind: "eligible", outcome });
}
/**
 * Gates (0)–(8) of design §33.7 in order; (9) the pass move and the records are the coordinator's. Pure.
 *
 * | # | Check | Refused as | Pass back (OD-25) |
 * |---|---|---|---|
 * | 0–3 | {@link voiceAgentPassConversationGateOf} | | checked |
 * | 4 | the target is in the conversation's account and workspace | `target_not_found` | skipped |
 * | 5 | `live`, not archived, a live version | `target_not_live` | skipped |
 * | 6 | `mayPassBetween(source, target)` | `target_cannot_receive`, `audience_crossed` | skipped |
 * | — | the target's version loaded | `target_configuration_unavailable` | checked |
 * | 7 | the current language; every dialogue medium; the voice resolves | `target_language_unavailable`, `target_medium_unsupported`, `target_configuration_unavailable` | language and voice |
 * | 8 | open now, or answers at every hour (OD-21) | `target_outside_opening_hours` | skipped |
 * | 9 | the target's access list admits the party (plan-029 DD29-7, design §40.7.5) | `party_not_listed` | skipped |
 *
 * A pass back is the `pass_back` move only: a pass to a VoiceAgent already on the stack (an unwind) went through a link
 * the business configured and takes that link's full gate.
 */
export function voiceAgentPassEligibilityOf(facts) {
    const conversation = voiceAgentPassConversationGateOf(facts);
    if (conversation.kind === "unavailable")
        return conversation;
    const passBack = facts.move.kind === "pass_back";
    if (!passBack) {
        const target = facts.target;
        if (target === null)
            return unavailable("target_not_found");
        if (target.state !== "live" || target.archived || !target.liveVersionPresent)
            return unavailable("target_not_live");
        const refusal = passBetweenRefusalOf(facts.source, target.agentTemplate);
        if (refusal === "pass_target_audience_crossed")
            return unavailable("audience_crossed");
        if (refusal !== null)
            return unavailable("target_cannot_receive");
    }
    const config = facts.targetConfig;
    if (config === null)
        return unavailable("target_configuration_unavailable");
    if (!config.languages.includes(facts.currentLanguage))
        return unavailable("target_language_unavailable");
    if (!passBack && !config.mediaSupported)
        return unavailable("target_medium_unsupported");
    if (!config.voiceResolves)
        return unavailable("target_configuration_unavailable");
    if (!passBack && !config.openNow)
        return unavailable("target_outside_opening_hours");
    if (!passBack && facts.targetPartyAccess === "not_listed")
        return unavailable("party_not_listed");
    return conversation;
}
function unavailable(reason, limit) {
    return Object.freeze(limit === undefined ? { kind: "unavailable", reason } : { kind: "unavailable", reason, limit });
}
//# sourceMappingURL=voice-agent-pass.js.map